'use client';

import { getDictionary } from '@/app/dictionaries/getDictionary';
import { AgentLogger } from '@/utils/logger-agent'; // New import
import { Chat } from '@/utils/types';
import { XMarkIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdditionalInfoForm from './AdditionalInfoForm';
import { AddressForm } from './AddressForm';
import { getAgencyConfig } from './agency-config-helper'; // Import AgencyConfiguration
import ContactForm from './ContactForm';
import { IntroForm } from './IntroForm';
import MapTimeframeForm from './MapTimeframeForm';
import PropertyDetailsForm from './PropertyDetailsForm';
import PropertyTransactionForm from './PropertyTransactionForm';
import ReasoningLoader from './ReasoningLoader';
import ValuationForm from './ValuationForm';

// Define the updated sequence of steps for the progress bar
const AVM_STEPS = [
  'sales-avm-step1-intro',
  'sales-avm-step2-address',
  'sales-avm-step3-statbel',
  'sales-avm-step4-transaction',
  'sales-avm-step5-additional-info',
  'sales-avm-step6-map-timeframe',
  'sales-avm-step7-contact',
  'sales-avm-step8-valuation',
  'sales-avm-step9-final-evaluation',
];
const TOTAL_AVM_STEPS = AVM_STEPS.length;

type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  followUpQuestions?: string[];
  chatId?: string;
  createdAt?: Date;
  userId?: string;
  classification?: {
    primary_intent: string;
    workflowStep?: string;
  };
  id?: string;
  properties?: { 
    city?: string;
    propertyType?: string;
    livingArea?: number;
    bedrooms?: number;
    bathrooms?: number;
    plotArea?: number;
    [key: string]: any;
  };
};

interface ChatConfig {
  agencyId?: string;
  agentId?: string;
  backgroundColor?: string;
  faviconUrl?: string;
  enableMfa?: boolean;
  agencyEmail?: string;
  agencyName?: string;
  agencyLanguage?: string;
  textStyle?: React.CSSProperties;
  defaultLocale?: 'nl' | 'fr' | 'en';
  styling?: React.CSSProperties;
  startOpen?: boolean;
  // agency configuration
  top?: React.ComponentType<any>;
  left?: React.ComponentType<any>;
  widgetTitleOverrideKey?: string;
  headerTitleOverrideKey?: string;
  // Other configuration
  options?: {
    animationDuration?: number;
  };
  closedWidgetCloseButtonClassName?: string; // Add optional className for close button
  closedWidgetCloseIconClassName?: string; // Add optional className for close icon
  preventRefreshOnUrlChange?: boolean; // Add optional flag for preventing refresh
}

interface ChatProps {
  defaultLocale: string;
}

// Define the structure for comparable properties
interface ComparableProperty {
  id: string;
  lat: number;
  lng: number;
  price: number;
  address: string;
  livingArea: number;
  bedrooms: number;
  energyClass: string;
  plotSurface: number;
  buildingConditionScore: number;
  numberOfFrontages: number;
}

// Define ContactInfo type used in Step 6
interface ContactInfo {
  name?: string; // Add optional name
  phone: string;
}

// Create a logger instance for AVM agent
const logger = new AgentLogger('avm-agent');

function AvmAgent({ defaultLocale }: ChatProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // --- Add ref for the last message ---
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dictionary, setDictionary] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [guestId, setGuestId] = useState<string | undefined>(undefined);
  const [detectedLanguage, setDetectedLanguage] = useState<string>('nl'); // Default to English
  const [urlConfig, setUrlConfig] = useState<ChatConfig | null>(null);
  const [locale, setLocale] = useState<string>('nl');
  const [agentId, setAgentId] = useState<string | null>(null);
  const [chatBackgroundColor, setChatBackgroundColor] = useState<string>('#ffffff'); // State for background color, default to white
  const [chatTextStyle, setChatTextStyle] = useState<React.CSSProperties>({});
  const [isReady, setIsReady] = useState(false); // State to track initial loading
  const [chatFaviconUrl, setChatFaviconUrl] = useState<string | null>(null); // State for favicon URL, default null
  const [avmFetchPromise, setAvmFetchPromise] = useState<Promise<any> | null>(null); // State to hold the background AVM fetch promise
  // --- Add state for phone verification persistence ---
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  // --- State to force contact form open on Step 6 --- (REMOVED)

  const [sellingState, setSellingState] = useState<{
    contactInfo?: ContactInfo; // Updated type
    privacyConsent?: boolean;
    propertyInfo?: {
      epcValue: string;
      condition: string;
    };
    propertyDetails?: {
      livingArea: number;
      bedrooms: number;
      bathrooms: number;
      plotArea?: number;
      facade?: number;
      transactionCount?: number;
    };
    streetAddress?: string;
    zipCode?: string;
    city?: string;
    propertyType?: 'apartment' | 'house';
    isInterestedInValuation?: boolean;
    additionalDetails?: any;
    priceRange?: { min: number; max: number };
    avmResult?: any;
    comparables?: ComparableProperty[];
    subject_latitude?: number;
    subject_longitude?: number;
    latitude?: number;
    longitude?: number;
    // --- Add star rating to state --- 
    starRating?: number;
    // --- Add flag for details response (optional, added in previous step but ensure it's here) ---
    wantsDetails?: boolean;
    // --- Add selling timeframe ---
    sellingTimeframe?: 'now' | '6m' | '12m' | 'curious';
  }>({});

  // Function to get config from URL parameters
  const getConfigFromUrl = (): ChatConfig => {
    if (typeof window === 'undefined') return {};
  
    try {
    // Try URL parameters
      const params = new URLSearchParams(window.location.search);
      const encodedParentUrl = params.get('config');
      if (encodedParentUrl) {
        const config = JSON.parse(encodedParentUrl);
        return config;
      }
    } catch (error) {
      console.error('Error parsing config from URL:', error);
    }
    return {};
  };

  const refreshChat = useCallback(() => {
    setHasInitialized(false);
    setIsChatOpen(false);
    setChatId(undefined);
    setGuestId(undefined);
    setMessages([{ // Reset to the initial state (IntroForm trigger)
      role: 'assistant',
      content: '|SHOW_SELLING_FORM',
      classification: {
        primary_intent: 'initiate_sales_avm',
        workflowStep: 'sales-avm-step1-intro'
      }
    }]);
  }, []);
  
  useEffect(() => {
    const fetchDictionary = async (config: ChatConfig) => {
      if (!config?.agentId) {
        throw new Error('Agent ID missing during initialization');
      }

      setAgentId(config.agentId);
      setChatBackgroundColor(config?.backgroundColor ?? '#ffffff');
      setChatFaviconUrl(config?.faviconUrl ?? null);
      setChatTextStyle(config?.textStyle ?? {});
      setLocale(config?.defaultLocale ?? 'nl');

      let questions = null;
      if (config?.agencyId) {
        const response = await fetch(`/api/questions/${config.agencyId}?locale=${config?.defaultLocale ?? locale}`);
        if (response.ok) {
          questions = await response.json();
        }
      }

      const dict = await getDictionary(config?.defaultLocale ?? locale, questions);
      setDictionary(dict);

      // Set the initial message to trigger the IntroForm immediately
      setMessages([{
        role: 'assistant',
        content: '|SHOW_SELLING_FORM', // Flag to activate form rendering logic
        classification: {
          primary_intent: 'initiate_sales_avm', // Indicate the starting intent
          workflowStep: 'sales-avm-step1-intro' // Specify the first step
        }
      }]);

      setIsReady(true);
    };

    // Get config from URL parameters on component mount
    if (!urlConfig) {
      const config = getConfigFromUrl();
      const agencyConfig = getAgencyConfig(config?.agencyId);
      const finalConfig = { ...config, ...agencyConfig };
      setUrlConfig(finalConfig);
      setIsChatOpen(finalConfig?.startOpen ?? false);
      fetchDictionary(finalConfig);
    } else {
      fetchDictionary(urlConfig);
    }
  }, [urlConfig, locale]);

  useEffect(() => {
    if (!isLoading) {
      if (inputRef.current) {
        inputRef.current?.focus(); // Focus after a slight delay
      }
    }
  }, [isLoading]);

  // Always scroll to bottom on new messages
  // useEffect(() => {
  //   // --- Use scrollIntoView on the last message element --- 
  //   if (lastMessageRef.current) {
  //     // Simple scrollIntoView call
  //     lastMessageRef.current.scrollIntoView(); 
  //   }
  // }, [messages]); // Dependency array includes messages
  // ---- REMOVED this hook as MutationObserver handles it ----

  // --- Auto-scroll effect using MutationObserver ---
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Function to scroll smoothly to the bottom
    const scrollToBottom = () => {
      // Use requestAnimationFrame to wait for next paint cycle
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    };

    // Initial scroll to bottom when the effect runs
    scrollToBottom();

    // Create an observer instance linked to a callback function
    const observer = new MutationObserver((mutationsList) => {
      // --- Check if ANY mutation in this batch originated from the map ---
      let shouldScroll = true; // Assume we should scroll by default
      for (const mutation of mutationsList) {
        // Check if the mutation target or its parent is the map area
        if (mutation.target instanceof Node && mutation.target.nodeType === Node.ELEMENT_NODE) {
          // Check the target element itself
          if ((mutation.target as HTMLElement).closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation, no need to check others in this batch
          }
        }
        // Also check added nodes, as the target might be the messages container
        // but a map node was added
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLElement && addedNode.closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation
          }
        }
        // If we've decided not to scroll, exit the outer loop too
        if (!shouldScroll) break; 
      }

      // --- Only scroll if no map-related mutations were detected ---
      if (shouldScroll) {
        scrollToBottom();
      }
    });

    // Start observing the target node for configured mutations
    // --- Observe attributes and characterData as well ---
    const config = { childList: true, subtree: true, attributes: true, characterData: true };
    observer.observe(container, config);

    // Cleanup function to stop observing when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, [messages]); // Re-run when messages array changes to ensure observer is attached
  // --- End Auto-scroll effect ---

  // Save message to DB
  const saveMessage = useCallback(async (message: Message) => {
    if (!message.chatId || !message.userId) {
      console.error('Cannot save message:', message);
      return;
    }

    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...message,
          createdAt: new Date(),
        }),
      });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  }, []);

  const formatMessage = useCallback((content: string) => {
    // Handle bold text
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Clean up multiple newlines first
    content = content.replace(/\n{3,}/g, '\n\n'); // Replace 3+ newlines with 2
    
    // Preserve double line breaks and handle bullet points
    return content
      .split(/\n\n/) // Split on double line breaks first
      .map(paragraph => {
        if (paragraph.trim() === '') return '<div class="h-4"></div>'; // Increased spacing
        const lines = paragraph.split('\n').map(line => {
          line = line.trim(); // Trim each line
          if (line.startsWith('• ')) return `<div class="pl-2">${line}</div>`;
          if (line.startsWith('○ ')) return `<div class="pl-4">${line}</div>`;
          if (line.startsWith('- ')) return `<div class="pl-8">${line.replace(/^- /, '- ')}</div>`; // Add more indent for sub-sub-points
          return `<div>${line}</div>`;
        });
        return lines.join('');
      })
      .join('<div class="h-4"></div>'); // Add spacing between paragraphs
  }, []);

  const minimizeChat = () => setIsChatOpen(false);

  // Helper function to handle the deterministic form transition
  const handleDeterministicStep = async (currentStep: string, userMessageContent: string, formData?: any) => {
    setIsLoading(true);

    const newUserMessage: Message = {
      role: 'user',
      content: userMessageContent,
      chatId: chatId,
      userId: guestId,
      classification: { primary_intent: 'provide_data', workflowStep: currentStep }
    };

    if (!hasInitialized) {
      logger.error('Chat not initialized when trying to submit form data.', { currentStep });
      setError('Chat session error. Please refresh.');
      setIsLoading(false);
      return;
    }
    await saveMessage(newUserMessage);
    setMessages(prev => [...prev, newUserMessage]);

    let nextStep: string | null = null;
    let avmResultData: any = null; // To store AVM result if called

    // Special handling for end of step 6 (Contact Info)
    if (currentStep === 'sales-avm-step6-contact') {
      logger.info('Step 6 finished. Calling /api/avm for preliminary valuation.');
      try {
        // Construct payload for /api/avm
        const avmPayload = {
          // Ensure all necessary data from sellingState is included
          // Use defaults or handle missing data gracefully
          'Postal Code': sellingState.zipCode || '',
          'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
          'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
          'Property Type': sellingState.propertyType || 'house',
          'Address': `${sellingState.streetAddress}, ${sellingState.zipCode} ${sellingState.city}`,
          'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
          'New Construction': false, // Assuming false unless captured
          'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
          'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
          'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
          'Latitude': sellingState.latitude,
          'Longitude': sellingState.longitude,
          // Add any other required fields for your /api/avm endpoint
        };

        // --- Add a check/log for missing coordinates --- START
        if (sellingState.latitude === undefined || sellingState.longitude === undefined) {
          logger.warn('Latitude or Longitude missing from sellingState before sending AVM payload', {
            latitude: sellingState.latitude,
            longitude: sellingState.longitude,
            currentState: sellingState
          });
          // Potentially add error handling here if needed, though the Python script might still geocode
        }
        // --- Add a check/log for missing coordinates --- END

        const avmResponse = await fetch('/api/avm', { // Assuming this is your AVM endpoint
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] }) // Adjust payload structure if needed
        });

        avmResultData = await avmResponse.json(); // Store the full result
        if (!avmResponse.ok) {
          throw new Error(`AVM API returned ${JSON.stringify(avmResultData)}`);
        }
        logger.info('Received AVM result', { avmResultData });

        // Extract price range (adjust based on actual API response structure)
        const priceRange = avmResultData?.priceRange || avmResultData?.valuation?.priceRange || { min: 0, max: 0 };
        setSellingState(prev => ({ ...prev, priceRange, avmResult: avmResultData }));

        // Now call workflow to get the next step (should be step 7)
        const workflowResponse = await fetch('/api/avm/workflow', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentStep }),
        });

        if (!workflowResponse.ok) {
          throw new Error(`Workflow API returned ${workflowResponse.status} after AVM call`);
        }
        const workflowData = await workflowResponse.json();
        nextStep = workflowData.nextStep; // Should be 'sales-avm-step7-valuation'

      } catch (avmError) {
        logger.error('Error during AVM call after step 6:', avmError);
        setError('Failed to get property valuation. Please try again or contact support.');
        setIsLoading(false);
        return;
      }
    } else {
      // For all other steps, just call the workflow endpoint
      try {
        const workflowResponse = await fetch('/api/avm/workflow', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentStep }),
        });

        if (!workflowResponse.ok) {
          throw new Error(`Workflow API returned ${workflowResponse.status}`);
        }
        const workflowData = await workflowResponse.json();
        nextStep = workflowData.nextStep;
      } catch (error) {
        logger.error('Error calling workflow endpoint or processing response:', error);
        setError('Failed to determine next step. Please try again.');
        setIsLoading(false);
        return;
      }
    }

    // Handle the next step
    if (nextStep && nextStep === 'sales-avm-step9-final-evaluation') {
      // --- If workflow says next is Step 9, trigger final message --- 
      logger.info('Workflow indicates next step is final evaluation. Displaying final message.');
      // Format the final message using the priceRange from the current state
      const finalMessageContent = (dictionary?.sellingForm?.finalThankYou || 
          'Thank you! Your valuation estimate is between €[MIN_PRICE] and €[MAX_PRICE]. We will be in touch.')
        .replace('[MIN_PRICE]', sellingState.priceRange?.min?.toLocaleString(['nl-BE']) ?? 'N/A')
        .replace('[MAX_PRICE]', sellingState.priceRange?.max?.toLocaleString(['nl-BE']) ?? 'N/A');
          
      const finalAssistantMessage: Message = {
        role: 'assistant',
        content: finalMessageContent,
        chatId: chatId,
        userId: agentId as string,
        classification: { primary_intent: 'sales_avm_complete', workflowStep: 'completed' }
      };

      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
      setMessages(prev => [...prev, finalAssistantMessage]);
      await saveMessage(finalAssistantMessage);
      setIsLoading(false);

    } else if (nextStep) { // If workflow provides a different next step (next form to show)
      // --- Standard next step logic --- 
      logger.info(`Workflow determined next step: ${nextStep}. Rendering next form directly.`);
      const nextAssistantMessage: Message = {
        role: 'assistant',
        content: '|SHOW_SELLING_FORM', 
        chatId: chatId,
        userId: agentId as string,
        classification: { primary_intent: 'sales_avm', workflowStep: nextStep }
        // Optionally add AVM result to message if needed for display
        // properties: nextStep === 'sales-avm-step7-valuation' ? avmResultData : undefined
      };

      // Add a delay for visual feedback
      await new Promise(resolve => setTimeout(resolve, 800));

      // Directly update UI and save
      setMessages(prev => [...prev, nextAssistantMessage]);
      await saveMessage(nextAssistantMessage);
      setIsLoading(false);

    } else if (nextStep === null && currentStep === 'sales-avm-step9-final-evaluation') {      // --- This block is now effectively handled by the logic above --- 
      // Keep it potentially as a fallback or remove if confident.
      // For now, let's log if we ever reach here unexpectedly.
      logger.warn('Reached final evaluation processing block unexpectedly.');
      // ... existing final message logic ... (can likely be removed)
      setIsLoading(false);
    } else {
      // Unexpected response from workflow API or end condition met prematurely
      logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} from ${currentStep}.`);
      setError('Could not determine the next step automatically.');
      setIsLoading(false);
    }
  };

  const renderSellingForm = (message: Message) => {
    const currentMessage = messages[messages.length - 1];
    const isCurrentMessage = message === currentMessage;

    logger.debug('Attempting to render selling form', {
      hasShowFormFlag: message.content?.includes('|SHOW_SELLING_FORM'),
      workflowStep: message.classification?.workflowStep,
      messageContent: message.content?.substring(0, 100)
    });

    if (!message.content?.includes('|SHOW_SELLING_FORM')) {
      logger.debug('Not showing form - missing SHOW_SELLING_FORM flag');
      return null;
    }

    // Ensure dictionary is loaded before rendering any specific form
    if (!dictionary) {
      logger.warn('Dictionary not loaded, cannot render form');
      return <div className="text-sm text-gray-500">Loading form...</div>; // Or some placeholder
    }

    switch (message.classification?.workflowStep) {
      case 'sales-avm-step1-intro':
        logger.info('Rendering intro form', {
          hasDictionary: !!dictionary,
          hasSellingForm: !!dictionary?.sellingForm,
          translations: {
            welcomeMessage: dictionary?.sellingForm?.welcomeMessage,
            readyButton: dictionary?.sellingForm?.readyButton
          },
          locale,
          detectedLanguage
        });

        return (
          <IntroForm
            dictionary={dictionary}
            agencyId={urlConfig?.agencyId}
            onSubmit={async () => {
              const currentStep = 'sales-avm-step1-intro';
              const readyResponseText = dictionary?.sellingForm?.readyResponse || 'Yes';
              logger.debug('Intro form submitted, preparing to update UI and call workflow', { currentStep });

              // Create the user message object first
              const newUserMessage: Message = {
                role: 'user' as const,
                content: readyResponseText,
                chatId: chatId, // Use state chatId (might be undefined initially)
                userId: guestId, // Use state guestId (might be undefined initially)
                classification: { primary_intent: 'affirm', workflowStep: currentStep }
              };

              // --- Optimized Immediate UI Update (User Message Only) --- 
              setMessages(prev => [...prev, newUserMessage]);
              setIsLoading(true); // Start loading indicator immediately
              
              // --- Delayed UI Update (Next Form) & Background Processing --- 
              
              // Define the background task function
              const initializeAndSave = async () => {
                let definitiveChatId = chatId;
                let definitiveGuestId = guestId;
                let justInitialized = false;

                try {
                  // 1. Initialize if needed (can run concurrently with timeout)
                  if (!hasInitialized) {
                    logger.info('Chat not initialized, calling /api/conversations');
                    if (!agentId) throw new Error('Agent ID missing during chat initialization');
                    
                    const initResponse = await fetch('/api/conversations', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ agencyId: urlConfig?.agencyId, agentId, type: 'CHAT' } as Chat),
                    });
                    if (!initResponse.ok) {
                      throw new Error(`Failed to init chat via API: ${initResponse.statusText}`);
                    }
                    
                    const chatData = await initResponse.json();
                    definitiveChatId = chatData.chatId;
                    definitiveGuestId = chatData.guestId;
                    justInitialized = true; 
                    
                    logger.info('Chat initialized successfully', { chatId: definitiveChatId, guestId: definitiveGuestId });

                    // Update state *after* successful initialization
                    setChatId(definitiveChatId);
                    if (!localStorage.getItem('chatId')) {
                      localStorage.setItem('chatId', definitiveChatId as string);
                    }
                    setGuestId(definitiveGuestId);
                    setHasInitialized(true); 
                  }

                  // 2. Save the user message (now with definitive IDs)
                  if (definitiveChatId && definitiveGuestId) {
                    await saveMessage({...newUserMessage, chatId: definitiveChatId, userId: definitiveGuestId });
                  } else {
                    logger.error('Cannot save user message, missing chatId or guestId after initialization attempt');
                    throw new Error('Chat identification failed.'); // Throw to prevent saving assistant msg
                  }

                  // 3. Prepare the assistant message for the next step
                  const nextAssistantMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM',
                    chatId: definitiveChatId, // Use the definitive chatId
                    userId: agentId as string, 
                    classification: { primary_intent: 'sales_avm', workflowStep: 'sales-avm-step2-address' }
                  };

                  // 4. Save the assistant message (using definitive IDs)
                  if (definitiveChatId && agentId) {
                    await saveMessage(nextAssistantMessage);
                  } else {
                    logger.error('Cannot save assistant message, missing chatId or agentId');
                    // Potentially throw an error here too
                  }

                  // Return the prepared assistant message for the timeout callback
                  return nextAssistantMessage;

                } catch (error) {
                  logger.error('Error during intro form background processing:', error);
                  setError(`Failed to initialize or save initial messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  setIsLoading(false); // Ensure loading stops on error
                  return null; // Indicate failure
                }
              };

              // Start the background task immediately
              const backgroundPromise = initializeAndSave();

              // --- Introduce a 1-second delay before showing the next form --- 
              setTimeout(async () => {
                try {
                  // Wait for the background task to potentially finish getting IDs and prepare the message
                  const nextAssistantMessage = await backgroundPromise;

                  if (nextAssistantMessage) {
                    // Append the Step 6 form message instead of replacing
                    setMessages(prev => [...prev, nextAssistantMessage]);
                  } else {
                    // If backgroundPromise resolved to null (error occurred), 
                    // ensure loading is false, error state should already be set.
                    logger.warn('Background task failed, not showing next step form.');
                  }
                } catch (error) {
                  // Catch potential errors from the backgroundPromise itself if it rejects unexpectedly
                  logger.error('Error resolving background task promise in timeout:', error);
                  setError('An unexpected error occurred while preparing the next step.');
                  setIsLoading(false);
                } finally {
                  // Set loading false *after* the timeout and potential UI update, 
                  // only if the background task didn't already set it false due to error.
                  // However, the background task's finally block might run after this in some cases.
                  // Let's rely on the background task's finally block to set loading false.
                  //setIsLoading(false); 
                  // Correction: We need isLoading to be false *after* the form appears to enable it.
                  // The background task's finally block handles the case where init/save finishes *before* the timeout.
                  // This timeout's finally block handles the case where init/save finishes *after* the timeout.
                  const finalAssistantMessage = await backgroundPromise.catch(() => null); // Re-check promise state
                  if (finalAssistantMessage) { // Only set loading false if background task succeeded
                    setIsLoading(false); 
                  }
                }
              }, 800); // 800ms = 0.8 second delay

            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step2-address':
        logger.info('Rendering address form', {
          hasDictionary: !!dictionary,
          hasSellingForm: !!dictionary?.sellingForm,
          translations: {
            propertyAddress: dictionary?.sellingForm?.propertyAddress,
            propertyType: dictionary?.sellingForm?.propertyType
          },
          locale,
          detectedLanguage
        });

        return (
          <AddressForm
            dictionary={dictionary}
            onSubmit={({ streetAddress, zipCode, city, propertyType, latitude, longitude }) => {
              const currentStep = 'sales-avm-step2-address';
              const address = `${streetAddress}, ${zipCode} ${city}`;
              logger.debug('Address form submitted', { address, propertyType, latitude, longitude });
              setSellingState(prev => ({ 
                ...prev, 
                streetAddress, 
                zipCode, 
                city, 
                propertyType, 
                latitude,
                longitude
              }));
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided || 'Here is my property information:'}\n• ${dictionary.sellingForm.propertyAddress || 'Address'}: ${address}\n• ${dictionary.sellingForm.propertyType || 'Property type'}: ${propertyType === 'apartment' ? dictionary.sellingForm.propertyTypeApartment : dictionary.sellingForm.propertyTypeHouse}` :
                `Here is my property information:\n• Address: ${address}\n• Property type: ${propertyType}`;
              handleDeterministicStep(currentStep, formattedMessage, { streetAddress, zipCode, city, propertyType });
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step3-statbel':
        logger.info('Rendering property details form');
        return (
          <PropertyDetailsForm
            dictionary={dictionary}
            propertyType={sellingState.propertyType || 'apartment'}
            municipality={sellingState.zipCode || ''}
            cityName={sellingState.city || ''}
            onSubmit={async (propertyDetails) => {
              const currentStep = 'sales-avm-step3-statbel';
              setSellingState(prev => ({ ...prev, propertyDetails }));

              // Format the message with property details
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyDetailsProvided}\n` +
                `• ${dictionary.sellingForm.livingArea}: ${propertyDetails.livingArea} m²\n` +
                `• ${dictionary.sellingForm.bedrooms}: ${propertyDetails.bedrooms}\n` +
                `• ${dictionary.sellingForm.bathrooms}: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• ${dictionary.sellingForm.plotArea}: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• ${dictionary.sellingForm.facade}: ${propertyDetails.facade}\n` : '')
                :
                'Here are my property details:\n' +
                `• Living Area: ${propertyDetails.livingArea} m²\n` +
                `• Bedrooms: ${propertyDetails.bedrooms}\n` +
                `• Bathrooms: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• Plot Area: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• Facade: ${propertyDetails.facade}\n` : '');
              handleDeterministicStep(currentStep, formattedMessage, propertyDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step4-transaction':
        logger.info('Rendering property transaction form');
        return (
          <PropertyTransactionForm
            dictionary={dictionary}
            transactionCount={sellingState.propertyDetails?.transactionCount}
            onSubmit={(propertyInfo) => {
              const currentStep = 'sales-avm-step4-transaction';
              setSellingState(prev => ({ ...prev, propertyInfo }));
              const translatedEpcValue = propertyInfo.epcValue === 'unknown' ? dictionary?.sellingForm?.propertyEpcUnknown : propertyInfo.epcValue;
              const conditionKey = `condition${propertyInfo.condition}`; 
              const translatedCondition = dictionary?.propertyForm?.[conditionKey] || propertyInfo.condition;
              const translatedPropertyInfo = {
                [dictionary?.sellingForm?.propertyEpc || 'EPC value']: translatedEpcValue,
                [dictionary?.sellingForm?.propertyCondition || 'condition']: translatedCondition
              };
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided}\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}` :
                `Here are my property details:\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}`;

              // ---- New Logic for Step 4 Submission ----
              setError(null); // Clear previous error

              // 1. Construct Payload for /api/avm
              // Ensure all necessary data from sellingState is included
              const avmPayload = {
                'Postal Code': sellingState.zipCode || '',
                'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                'Building Condition Score': propertyInfo.condition ? parseInt(propertyInfo.condition) : 3,
                'Property Type': sellingState.propertyType || 'house',
                'Address': `${sellingState.streetAddress}, ${sellingState.zipCode} ${sellingState.city}`,
                'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                'New Construction': false, // Assuming false unless captured
                'Energy Class': propertyInfo.epcValue || 'unknown',
                'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
                'Latitude': sellingState.latitude,
                'Longitude': sellingState.longitude,
                // Add any other required fields for your /api/avm endpoint
              };

              // --- Add a check/log for missing coordinates --- START
              if (sellingState.latitude === undefined || sellingState.longitude === undefined) {
                logger.warn('Latitude or Longitude missing from sellingState before sending AVM payload', {
                  latitude: sellingState.latitude,
                  longitude: sellingState.longitude,
                  currentState: sellingState
                });
                // Potentially add error handling here if needed, though the Python script might still geocode
              }
              // --- Add a check/log for missing coordinates --- END

              // Create the user message object (but don't send yet)
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep }
              };

              // Background task: Save user message, call AVM, update state, call workflow, add next step
              const processStep4 = async () => {
                try {
                  // --- New Flow: Workflow first, then background AVM fetch --- 

                  // Save the user message first
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  // Update UI immediately with user message
                  setMessages(prev => [...prev, newUserMessage]);

                  // 1. Call workflow FIRST to get next step ID
                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status}`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  // 2. Initiate AVM fetch in the background (DO NOT AWAIT)
                  logger.info('Initiating background AVM fetch...', { payload: avmPayload });
                  const avmPromise = fetch('/api/avm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] })
                  })
                    .then(async (response) => { // Add basic processing to check status and parse JSON
                      if (!response.ok) {
                        const errorData = await response.json();
                        logger.error('Background AVM fetch failed', { status: response.status, error: JSON.stringify(errorData) });
                        throw new Error(`Background AVM fetch failed: ${response.status} ${JSON.stringify(errorData)}`);
                      }
                      const data = await response.json();
                      // Validate required fields within the promise result
                      if (!data.properties || data.subject_latitude === undefined || data.subject_longitude === undefined) {
                        logger.error('Background AVM fetch response missing required data', { response: data });
                        throw new Error('Background AVM fetch response missing required data.');
                      }
                      logger.info('Background AVM fetch succeeded.');
                      return data; // Resolve with the parsed and validated data
                    })
                    .catch(error => {
                      logger.error('Caught error during background AVM fetch promise', { error });
                      // Ensure the promise rejects with an error so await in Step 5 catches it
                      throw error; 
                    });

                  // 3. Store the promise in state
                  setAvmFetchPromise(avmPromise);

                  // 4. Proceed immediately to the next step (Step 5)
                  if (nextStep === 'sales-avm-step5-additional-info') {
                    logger.info(`Workflow determined next step: ${nextStep}. Rendering AdditionalInfoForm directly.`);
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep }
                    };

                    // Add a delay for visual feedback
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Update UI with next step
                    setMessages(prev => [...prev, nextAssistantMessage]);
                    // Save the assistant message
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 4.`);
                    setError('Could not determine the next step automatically.');
                  }

                } catch (error) {
                  // Error handling now focuses on workflow call or saving message
                  logger.error('Error during Step 4 processing (workflow call or saving message):', error);
                  setError(`Failed to process property details: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  // Optionally add an error message to the chat
                }
              };

              // Start the background processing
              processStep4();
              // ---- End New Logic for Step 4 ----
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step5-additional-info':
        logger.info('Rendering AdditionalInfoForm', {
          hasDictionary: !!dictionary,
          hasSellingForm: !!dictionary?.sellingForm,
          translations: { 
            question: dictionary?.sellingForm?.additionalInfoQuestion,
            placeholder: dictionary?.sellingForm?.additionalDetailsPlaceholder 
          }
        });
        return (
          <AdditionalInfoForm
            dictionary={dictionary}
            onSubmit={(additionalDetails: string) => {
              const currentStep = 'sales-avm-step5-additional-info';
              const formattedMessage = dictionary?.sellingForm ? 
                additionalDetails ? 
                  `${dictionary.sellingForm.additionalDetailsProvided || 'Provided details:'}\n${additionalDetails}` : 
                  (dictionary?.sellingForm?.noAdditionalDetailsProvided || 'No additional details provided.')
                :
                additionalDetails ?
                  `Here are my additional details:\n${additionalDetails}` :
                  'No additional details provided.';
               
              // --- Update Logic for Step 5 Submission ---
              setIsLoading(true);
              setError(null);

              // Create user message object
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep }
              };

              // --- Add user message immediately ---
              setMessages(prev => [...prev, newUserMessage]);

              // --- Define background task with updated loader logic --- 
              const processStep5 = async (detailsForReasoning: string) => { 
                try {
                  // --- Save user message (can happen concurrently) --- 
                  if (chatId && guestId) {
                    saveMessage(newUserMessage); // Fire and forget for now
                  }

                  // --- Prepare data for Reasoning Loader --- 
                  const city = sellingState.city || '';
                  const propTypeRaw = sellingState.propertyType || 'property';
                  const propType = propTypeRaw === 'house' ? 
                    (dictionary?.sellingForm?.propertyTypeHouse || 'house') : 
                    (dictionary?.sellingForm?.propertyTypeApartment || 'apartment');
                  const livingArea = sellingState.propertyDetails?.livingArea || 0;
                  const bedrooms = sellingState.propertyDetails?.bedrooms;
                  const bathrooms = sellingState.propertyDetails?.bathrooms;
                  const plotArea = sellingState.propertyDetails?.plotArea;

                  // --- Create and Add the SINGLE Reasoning Loader Message --- 
                  const reasoningStartMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_REASONING_LOADER', // Flag to render the component
                    chatId: chatId,
                    userId: agentId as string,
                    classification: { primary_intent: 'system_reasoning', workflowStep: 'reasoning_start' }, 
                    properties: { 
                      city,
                      propertyType: propType,
                      livingArea,
                      bedrooms,
                      bathrooms,
                      plotArea,
                      additionalDetails: detailsForReasoning
                    },
                    // Assign temporary ID for React key purposes
                    id: `temp-reasoning-${Date.now()}` 
                  };
                  
                  setMessages(prev => [...prev, reasoningStartMessage]);

                  // --- Calculate total wait time --- 
                  const hasAdditionalDetails = detailsForReasoning && detailsForReasoning.trim().length > 0;
                  const numberOfReasoningSteps = hasAdditionalDetails ? 4 : 3;
                  const totalWaitTime = (numberOfReasoningSteps * 1500);

                  logger.info(`Starting wait for ${totalWaitTime}ms before showing Step 6...`);

                  // --- Wait for the calculated duration THEN perform background work & update UI --- 
                  setTimeout(async () => {
                    logger.info('Wait finished. Awaiting background tasks...');
                    try {
                      // --- Background work ...
                      if (!avmFetchPromise) {
                        throw new Error('AVM fetch promise not found. Cannot proceed.');
                      }
                      const avmData = await avmFetchPromise;
                      logger.info('Background AVM fetch promise resolved.', { resultKeys: Object.keys(avmData) });

                      setSellingState(prev => ({
                        ...prev,
                        comparables: avmData.properties,
                        subject_latitude: avmData.subject_latitude,
                        subject_longitude: avmData.subject_longitude,
                        additionalDetails: detailsForReasoning 
                      }));

                      const workflowResponse = await fetch('/api/avm/workflow', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ currentStep }),
                      });

                      if (!workflowResponse.ok) {
                        throw new Error(`Workflow API returned ${workflowResponse.status} after awaiting AVM data`);
                      }
                      const { nextStep } = await workflowResponse.json();

                      if (nextStep === 'sales-avm-step6-map-timeframe') {
                        logger.info(`Workflow determined next step: ${nextStep}. Rendering MapTimeframeForm.`);
                        const nextAssistantMessage: Message = {
                          role: 'assistant',
                          content: '|SHOW_SELLING_FORM',
                          chatId: chatId,
                          userId: agentId as string,
                          classification: { primary_intent: 'sales_avm', workflowStep: nextStep }
                        };
                        setMessages(prev => [...prev, nextAssistantMessage]); // Just append
                        if (chatId && agentId) {
                          await saveMessage(nextAssistantMessage);
                        }
                      } else {
                        // Handle unexpected step, but DON'T remove the reasoning message
                        logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 5.`);
                        setError('Could not determine the next step automatically.');
                      }
                    } catch (error) {
                      // Handle error, but DON'T remove the reasoning message
                      logger.error('Error during Step 5 background processing (awaiting AVM or workflow):', error);
                      setError(`Failed to process additional info: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    } finally {
                      setIsLoading(false);
                      setAvmFetchPromise(null); 
                      logger.info('Step 5 processing complete.');
                    }
                  }, totalWaitTime); 

                } catch (outerError) {
                  logger.error('Error in synchronous part of Step 5 processing:', outerError);
                  setError(`An unexpected error occurred: ${outerError instanceof Error ? outerError.message : 'Unknown error'}`);
                  setIsLoading(false); 
                  setAvmFetchPromise(null); 
                }
              };

              // --- Start background processing, passing details --- 
              processStep5(additionalDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step6-map-timeframe':
        logger.info('Rendering MapTimeframeForm', {
          hasDictionary: !!dictionary,
          hasSellingForm: !!dictionary?.sellingForm,
          hasComparables: !!sellingState.comparables && sellingState.comparables.length > 0,
          hasCoords: !!sellingState.subject_latitude && !!sellingState.subject_longitude
        });
        return (
          <MapTimeframeForm
            dictionary={dictionary}
            comparables={sellingState.comparables || []}
            subject_latitude={sellingState.subject_latitude}
            subject_longitude={sellingState.subject_longitude}
            onSubmit={({ sellingTimeframe }) => {
              const currentStep = 'sales-avm-step6-map-timeframe';
              setSellingState(prev => ({ ...prev, sellingTimeframe }));
              
              // Find the label for the selected timeframe for a more user-friendly message
              const timeframeOptions = [
                { value: 'now', labelKey: 'timeframeNow', default: 'Now' },
                { value: '6m', labelKey: 'timeframe6m', default: 'In 6 months' },
                { value: '12m', labelKey: 'timeframe12m', default: 'In 12 months' },
                { value: 'curious', labelKey: 'timeframeCurious', default: 'I\'m just curious' },
              ];
              const selectedOption = timeframeOptions.find(o => o.value === sellingTimeframe);
              const timeframeLabel = dictionary?.sellingForm?.[selectedOption?.labelKey || ''] || selectedOption?.default || sellingTimeframe;
              
              const formattedMessage = `${dictionary?.sellingForm?.sellingTimeframeResponseTitle || 'Selling timeframe'}\n• ${timeframeLabel}`;

              handleDeterministicStep(currentStep, formattedMessage, { sellingTimeframe });
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step7-contact':
        logger.info('Rendering ContactForm');
        return (
          <ContactForm
            dictionary={dictionary}
            showNameInput={true}
            onSubmit={async (contactInfo: ContactInfo) => {
              const currentStep = 'sales-avm-step7-contact';
              setSellingState(prev => ({ ...prev, contactInfo })); // contactInfo now includes name
              // Updated formatted message to include name and phone
              let contactDetailsString = `• ${dictionary?.sellingForm?.contactPhone || 'Phone'}: ${contactInfo.phone}`;
              if (contactInfo.name) {
                contactDetailsString = `• ${dictionary?.sellingForm?.contactName || 'Name'}: ${contactInfo.name}\n` + contactDetailsString;
              }
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.contactProvided || 'Here is my contact information:'}\n${contactDetailsString}` :
                `Here is my contact information:\n${contactDetailsString}`;

              // --- Logic moved from old step 6 ---
              setIsLoading(true);
              setError(null);

              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep }
              };

              const processStep7 = async () => {
                try {
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  setMessages(prev => [...prev, newUserMessage]);

                  logger.info('Step 7 finished. Calling /api/avm/calculate-value', {
                    comparablesCount: sellingState.comparables?.length,
                    additionalDetails: sellingState.additionalDetails
                  });
                  const calculatePayload = {
                    comparables: sellingState.comparables || [],
                    additionalDetails: sellingState.additionalDetails || null,
                    subjectProperty: { 
                      'Postal Code': sellingState.zipCode || '',
                      'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                      'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
                      'Property Type': sellingState.propertyType || 'house',
                      'Address': `${sellingState.streetAddress}, ${sellingState.zipCode} ${sellingState.city}`,
                      'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                      'New Construction': false,
                      'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
                      'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                      'Bedrooms': sellingState.propertyDetails?.bedrooms || 0
                    },
                  };

                  const calculateResponse = await fetch('/api/avm/calculate-value', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(calculatePayload)
                  });

                  if (!calculateResponse.ok) {
                    const errorData = await calculateResponse.json();
                    throw new Error(`Calculate Value API failed with status ${calculateResponse.status}. ${JSON.stringify(errorData)}`);
                  }
                  const calculationData = await calculateResponse.json();

                  if (!calculationData.priceRange || !calculationData.priceRange.min || !calculationData.priceRange.max) {
                    logger.error('Calculate Value API response missing priceRange', { response: calculationData });
                    throw new Error('Calculate Value API response missing priceRange.');
                  }
                  
                  logger.info('Received priceRange from /api/avm/calculate-value', { priceRange: calculationData.priceRange });

                  const possibleRatings = [4, 4.5, 5];
                  const randomIndex = Math.floor(Math.random() * possibleRatings.length);
                  const calculatedStarRating = possibleRatings[randomIndex];
                  logger.info('Calculated star rating', { calculatedStarRating });
                  
                  setSellingState(prev => ({
                    ...prev,
                    priceRange: calculationData.priceRange,
                    starRating: calculatedStarRating,
                    contactInfo: contactInfo
                  }));

                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status} after calculating value`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  if (nextStep === 'sales-avm-step8-valuation') {
                    logger.info(`Workflow determined next step: ${nextStep}. Rendering ValuationForm directly.`);
                    const minPrice = calculationData.priceRange.min;
                    const maxPrice = calculationData.priceRange.max;
                    const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
                      'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
                    const valuationContent = valuationTemplate
                      .replace('[MIN_PRICE]', minPrice.toLocaleString(['nl-BE']))
                      .replace('[MAX_PRICE]', maxPrice.toLocaleString(['nl-BE']))
                      + ' |SHOW_SELLING_FORM';

                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: valuationContent,
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                      properties: {
                        priceRange: calculationData.priceRange
                      }
                    };

                    await new Promise(resolve => setTimeout(resolve, 500));
                    setMessages(prev => [...prev, nextAssistantMessage]);
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 7.`);
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep || 'unknown' }
                    };
                    await new Promise(resolve => setTimeout(resolve, 500));
                    setMessages(prev => [...prev, nextAssistantMessage]);
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }
                    if (!nextStep) {
                      setError('Could not determine the next step automatically.');
                    }
                  }

                  if (isPhoneVerified) {
                    await triggerAvmEmailNotification();
                  }

                } catch (error) {
                  logger.error('Error during Step 7 processing (Calculate Value or workflow):', error);
                  setError(`Failed to process contact information: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                  setIsLoading(false);
                }
              };

              processStep7();
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step8-valuation':
        logger.info('Rendering ValuationForm', { priceRange: sellingState.priceRange });
        
        // --- Define handler for Yes/No response ---
        const handleDetailsResponse = (response: 'yes' | 'no' | 'result') => {
          logger.info(`User responded '${response}' to details question. Saving silently.`);

          let silentUserMessage: Message;

          if (response === 'result') {
          // --- Construct the content WITH the price range using dictionary ---
            const minPrice = sellingState.priceRange?.min;
            const maxPrice = sellingState.priceRange?.max;
            // Use a template string from the dictionary if available
            const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
            'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
            const valuationContent = valuationTemplate
              .replace('[MIN_PRICE]', minPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
              .replace('[MAX_PRICE]', maxPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
            + ' |SHOW_SELLING_FORM'; // Append the flag

            const primaryIntent = 'valuation_result';

            silentUserMessage = {
              role: 'assistant',
              content: valuationContent, // <-- Use the new content with price range
              chatId: chatId,
              userId: agentId as string,
              classification: { 
                primary_intent: primaryIntent, 
                workflowStep: 'sales-avm-step8-valuation' 
              },
              // Store raw price range in properties for easier backend access if needed
              properties: {
                priceRange: sellingState.priceRange
              }
            };
          } else {
            const content = response === 'yes' 
              ? (dictionary?.sellingForm?.detailsYesButton || 'Yes') 
              : (dictionary?.sellingForm?.detailsNoButton || 'No');
            
            const primaryIntent = response === 'yes' ? 'affirm_details' : 'deny_details';
            
            silentUserMessage = {
              role: 'user',
              content: content,
              chatId: chatId,
              userId: guestId,
              classification: { 
                primary_intent: primaryIntent, 
                workflowStep: 'sales-avm-step8-details-response' // Specific step for this response
              },
              properties: { wantsDetails: response === 'yes' } // Store choice if needed
            };

            // Optionally update sellingState
            setSellingState(prev => ({ 
              ...prev, 
              wantsDetails: response === 'yes' 
            }));
          }
          
          
          // Save the message but DO NOT add it to the messages state
          saveMessage(silentUserMessage);

          // Optionally trigger something else here, e.g., a final non-form message or analysis
          // For now, just saving silently as requested.
        };
        
        return (
          <>
            <ValuationForm
              dictionary={dictionary}
              phoneNumber={sellingState.contactInfo?.phone || ''}
              priceRange={sellingState.priceRange}
              // --- Pass verification state and setter ---
              isVerified={isPhoneVerified}
              setIsVerified={setIsPhoneVerified}
              // --- Pass star rating from state --- 
              initialStarRating={sellingState.starRating}
              // --- Add onGoBack prop --- 
              onGoBack={() => {
                logger.info('User clicked Change Phone Number, reverting to Step 7 (Contact Form).');
                
                setMessages(prevMessages => {
                  // 1. Identify indices to remove
                  const lastMessageIndex = prevMessages.length - 1; // Index of current Step 8 trigger
                  let lastStep7AssistantIndex = -1;
                  let lastStep7UserAnswerIndex = -1;

                  // Find the index of the most recent previous Step 7 assistant trigger
                  for (let i = lastMessageIndex - 1; i >= 0; i--) { // Start search before the last message
                    if (prevMessages[i].role === 'assistant' && prevMessages[i].classification?.workflowStep === 'sales-avm-step7-contact') {
                      lastStep7AssistantIndex = i;
                      // Now find the user answer immediately following it
                      if (i + 1 < lastMessageIndex && 
                          prevMessages[i + 1].role === 'user' && 
                          prevMessages[i + 1].classification?.workflowStep === 'sales-avm-step7-contact') {
                        lastStep7UserAnswerIndex = i + 1;
                      } else {
                        logger.warn('Could not find user answer immediately after the last Step 7 assistant message.');
                      }
                      break; // Found the latest one
                    }
                  }

                  // 2. Filter out the identified messages
                  const filteredMessages = prevMessages.filter((_, index) => 
                    index !== lastMessageIndex && 
                    index !== lastStep7AssistantIndex &&
                    index !== lastStep7UserAnswerIndex
                  );

                  // 3. Create the new Step 7 message
                  const step7Message: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM', 
                    chatId: chatId,
                    userId: agentId as string,
                    classification: {
                      primary_intent: 'sales_avm',
                      workflowStep: 'sales-avm-step7-contact'
                    }
                  };

                  // 4. Return the new array
                  return [...filteredMessages, step7Message]; 
                });
              }}
              // --- Pass trigger function for successful verification --- 
              onVerificationSuccess={triggerAvmEmailNotification}
              // --- Pass the new handler ---
              onDetailsResponse={handleDetailsResponse} 
            />
            {/* --- Comment out the Continue button for Step 8 --- */}
            {/* 
            <button
              onClick={() => {
                const currentStep = 'sales-avm-step8-valuation';
                const confirmationMessage = dictionary?.sellingForm?.valuationConfirmed || 'Thank you for confirming.';
                handleDeterministicStep(currentStep, confirmationMessage, sellingState.priceRange);
              }}
              disabled={isLoading}
              className={`w-fit bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors mt-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
            >
              {dictionary?.sellingForm?.continueButton || 'Continue'}
            </button>
            */}
          </>
        );

      case 'sales-avm-step9-final-evaluation':
        logger.info('Reached final evaluation step display (renders null for now, handled by handleDeterministicStep).');
        return null;

      default:
        logger.debug('Not showing form - unknown workflow step');
        return null;
    }
  };

  // Determine current step for progress bar
  const lastFormMessage = messages
    .slice()
    .reverse()
    .find(m => m.role === 'assistant' && m.content?.includes('|SHOW_SELLING_FORM') && m.classification?.workflowStep && AVM_STEPS.includes(m.classification.workflowStep));

  const currentWorkflowStep = lastFormMessage?.classification?.workflowStep;
  const DISPLAY_TOTAL_STEPS = 8; // Still aiming for 8 visual steps

  let progressPercent = 0;
  let currentStepNumber = 0;

  if (currentWorkflowStep) {
    const stepIndex = AVM_STEPS.indexOf(currentWorkflowStep); // Index in the updated 9-step array

    if (stepIndex !== -1) {
      // Map the 9 logical steps (0-8) to 8 visual steps (1-8)
      // Step 0 (Intro) -> Visual Step 1
      // Step 1 (Address) -> Visual Step 2
      // Step 2 (Details) -> Visual Step 3
      // Step 3 (Transaction) -> Visual Step 4
      // Step 4 (Additional Info) -> Visual Step 5
      // Step 5 (Map+Timeframe) -> Visual Step 6
      // Step 6 (Contact) -> Visual Step 7
      // Step 7 (Valuation) -> Visual Step 8
      // Step 8 (Final Eval) -> Visual Step 8 (Completed)

      currentStepNumber = stepIndex + 1; // Start with 1-based index

      // Cap the displayed step number at DISPLAY_TOTAL_STEPS (8)
      if (currentStepNumber > DISPLAY_TOTAL_STEPS) {
        currentStepNumber = DISPLAY_TOTAL_STEPS;
      }

      // Calculate percentage based on the capped step number
      progressPercent = (currentStepNumber / DISPLAY_TOTAL_STEPS) * 100;

    } else if (currentWorkflowStep === 'completed') { // Final state after step 9 logic
      progressPercent = 100;
      currentStepNumber = DISPLAY_TOTAL_STEPS; // Show 8/8
    }
  }

  // --- Add triggerAvmEmailNotification function (adapted from BasicAgentv0) ---
  const triggerAvmEmailNotification = useCallback(async () => {
    logger.info('Phone verified, attempting to trigger AVM email notification.');

    try {
      // Prepare payload for the new endpoint
      const { comparables, ...sellingStateWithoutComparables } = sellingState;
      
      // Ensure contactInfo in the payload includes the name
      const sellingStateToSend = {
        ...sellingStateWithoutComparables,
        contactInfo: { // Explicitly include name and phone
          name: sellingState.contactInfo?.name,
          phone: sellingState.contactInfo?.phone,
        }
      };

      const payload = {
        locale: locale, // Pass current locale
        agencyId: urlConfig?.agencyId, // Pass agency ID from config
        chatId: chatId, // <-- Add chatId from state
        guestId: guestId, // <-- ADD THIS
        sellingState: sellingStateToSend // Pass the state object WITHOUT comparables, but WITH name in contactInfo
      };

      // --- Check if agencyId is present ---
      if (!payload.agencyId) {
        logger.error('Cannot trigger AVM email: agencyId is missing from urlConfig.');
        // Optionally inform the user or fallback differently?
        // setError("Configuration error: Missing agency identifier.");
        return; // Stop if no agencyId
      }

      logger.info('Triggering AVM email notification via /api/avm/trigger', {
        agencyId: payload.agencyId, // Log agencyId being sent
        locale: payload.locale,
        sellingStatePayload: sellingStateToSend // Log the sellingStateToSend payload
      });

      const response = await fetch('/api/avm/trigger', { // Use the new endpoint
        method: 'POST', // Ensure method is POST
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Send the collected data
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Error triggering AVM email: API responded with ${response.status}`, { errorText });
        // Optionally show an error to the user?
        // setError("Failed to send valuation summary email.");
      } else {
        const result = await response.json();
        logger.info('AVM email trigger successful', { result });
      }
    } catch (error) {
      logger.error('Error calling /api/avm/trigger fetch:', error);
      // Optionally show an error to the user?
      // setError("Failed to send valuation summary email.");
    }
  }, [sellingState, locale, urlConfig, chatId, guestId]); // Add dependencies: sellingState, locale, urlConfig, chatId, guestId

  useEffect(() => {
    logger.info('Agent component initialized', {
      locale
    });
  }, [locale]);

  // Create a function to send resize message
  const sendResizeMessage = useCallback((newWidth?: string, newHeight?: string) => {
    // Set dimensions based on viewport and chat state
    let width = newWidth;
    let height = newHeight;

    if (!newWidth && !newHeight) {
      width = isChatOpen ? '780px': '380px';
      height = isChatOpen ? '90%' : '150px';
    }
      
    // Use requestAnimationFrame to prevent flickering
    requestAnimationFrame(() => {
      window.parent.postMessage({
        type: 'RESIZE',
        width,
        height,
        isChatOpen
      }, '*');
    });
  }, [isChatOpen]);

  useEffect(() => {
    // Send resize message when chat state changes
    sendResizeMessage();
  }, [sendResizeMessage]);

  // Add this near your other useEffect hooks
  useEffect(() => {
    // Function to handle messages from parent
    const handleParentMessage = (event: MessageEvent) => {
      if (event.data.type === 'URL_CHANGED') {
        // Handle URL change
        const newUrl = event.data.url;
        console.log('Parent URL changed:', newUrl);

        // Capture the isReady state *before* any potential changes by refreshChat or other logic
        const widgetWasReadyBeforeThisUrlChange = isReady;

        try {
          const url = new URL(newUrl);
          const pathSegments = url.pathname.split('/');
          
          // Update locale if it changed
          const newLocale = pathSegments[1]?.toLowerCase().split('-')[0];
          const isLocaleValid = newLocale && /^(en|fr|nl)$/.test(newLocale);
          if (isLocaleValid) {
            setLocale(newLocale);
          }
        } catch (error) {
          console.error('Error parsing parent URL:', error);
        } finally {
          sendResizeMessage(); 
          
          // If preventRefreshOnUrlChange is true
          // AND the widget was NOT ready before this URL change (i.e., user had explicitly closed it),
          // then do NOT set it back to ready. Let it stay closed.
          if (urlConfig?.preventRefreshOnUrlChange && !widgetWasReadyBeforeThisUrlChange) {
            // Do nothing, isReady remains false (widget stays hidden as per user's last action)
          } else {
            // Ensure the widget is marked as ready to render (either the open chat or the minimized button).
            setIsReady(true);
          }
        }
      }
    };

    window.addEventListener('message', handleParentMessage);

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [sendResizeMessage, urlConfig, isReady]); 

  return (
    <>
      {!isReady ? (
        // Render nothing (or a loader) until dictionary is loaded
        null 
      ) : (
        <>
          {/* Chat Window - WhatsApp style */}
          {isChatOpen ? (
          // --- Apply mobile full-screen styles first, then override for md+ ---
            <div
              className={`
                fixed inset-0 w-screen h-[100dvh] z-[9999] pointer-events-auto
                transition-all duration-300 ease-out
                ${isChatOpen
              ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 scale-95 translate-y-8 pointer-events-none'}
                md:inset-auto md:bottom-6 md:right-6 md:w-[90%] md:h-[90%] md:max-w-md md:max-h-[700px]'
              `}
            >
              {/* --- Adjust close button position for mobile vs desktop --- */}
              <button
                onClick={minimizeChat}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center text-black hover:text-gray-700 transition-colors shadow-sm border border-gray-300 z-10
                             md:-top-2 md:-right-2"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>

              {/* --- Inner Container: Remove overflow-hidden --- */}
              <div
                ref={chatRef}
                className="w-full h-full bg-white flex flex-col pointer-events-auto font-sans border border-zinc-200
                             md:rounded-xl md:shadow-xl md:overflow-hidden"
                style={urlConfig?.styling}
              >
                {/* --- Header: Adjust padding/rounding for mobile --- */}
                <div 
                  className="relative flex flex-col px-3 py-4 
                               md:pt-4 md:pb-4 md:rounded-t-xl"
                >
                  {/* New Background Layer - Absolute positioning, bg color, target for gradient */}
                  <div 
                    className="absolute inset-0 header-background-layer overflow-hidden"
                    style={{
                      background: `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                    }}
                  ></div>

                  {/* Content goes here, after the background layer */}
                  {/* Add z-10 to ensure content stays above the absolute background */}
                  <div className="relative z-10 flex items-center gap-2"> 
                    {chatFaviconUrl && (
                      <div className="w-10 h-10 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center overflow-hidden">
                        <Image 
                          src={chatFaviconUrl} // Use state directly
                          alt="Agent Avatar" 
                          width={40} 
                          height={40} 
                          className="rounded-full w-full h-full object-contain" // Ensure image fits within wrapper
                          unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                        />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 md:text-base" style={chatTextStyle}>
                        {urlConfig?.headerTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.headerTitleOverrideKey]
                          ? dictionary.sellingForm[urlConfig.headerTitleOverrideKey]
                          : dictionary?.sellingForm?.headerTitle || 'Value my home...'}
                      </h3>
                    </div>
                  </div>

                  {/* Progress bar starts here */}
                  {(progressPercent > 0 || currentWorkflowStep === AVM_STEPS[0]) && (
                    <div className="relative z-10 mt-2 max-w-xs w-full mx-auto bg-white p-3 rounded-lg shadow-sm md:mt-2 md:p-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ 
                              width: `${progressPercent}%`,
                              backgroundColor: urlConfig?.styling?.backgroundColor || '#18181b'
                            }}
                          ></div>
                        </div>
                        {currentStepNumber > 0 && (
                          <span className="text-sm text-gray-900 font-medium whitespace-nowrap md:text-xs">
                            {dictionary?.chat?.progressStepLabel || 'Step'} {currentStepNumber} / {DISPLAY_TOTAL_STEPS}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  ref={messagesContainerRef}
                  className="flex-1 min-h-0 overflow-y-auto px-2 pt-2 pb-10 space-y-3 md:space-y-4 bg-white md:p-4 md:pb-10"
                  style={urlConfig?.styling}
                >
                  {messages.map((message, index) => {
                    const isFormMessage = message.role === 'assistant' && message.content?.includes('|SHOW_SELLING_FORM');
                    // --- Check for the reasoning loader flag --- 
                    const isReasoningMessage = message.role === 'assistant' && message.content === '|SHOW_REASONING_LOADER';

                    const assistantBaseClasses = 'bg-gray-100 text-black border border-gray-200';
                    const assistantMobileLayout = isFormMessage ? 'w-full' : 'max-w-[90%] mr-2';
                    const assistantDesktopLayout = 'md:max-w-[85%] md:mr-4';
                      
                    const isLastMessage = index === messages.length - 1;
                      
                    return (
                    // --- Attach ref conditionally, use stable key --- 
                      <div key={message.id || index} className="space-y-2" ref={isLastMessage ? lastMessageRef : null}>
                        <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`rounded-lg px-3 md:px-3 py-2 md:py-2 text-base ${
                              message.role === 'user'
                                ? 'bg-zinc-800 text-white max-w-[90%] md:max-w-[85%] ml-2 md:ml-4'
                                : `${assistantBaseClasses} ${assistantMobileLayout} ${assistantDesktopLayout}`
                            } shadow-sm`}
                            style={{
                              borderRadius: message.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
                            }}
                          >
                            {isFormMessage ? (
                              renderSellingForm(message)
                            ) : (
                            // --- Render ReasoningLoader if it's the reasoning message --- 
                              isReasoningMessage ? (
                                <ReasoningLoader 
                                  dictionary={dictionary} 
                                  // --- Pass data from message properties --- 
                                  city={message.properties?.city || ''}
                                  propertyType={message.properties?.propertyType || ''}
                                  livingArea={message.properties?.livingArea || 0}
                                  // --- Pass new properties with defaults --- 
                                  bedrooms={message.properties?.bedrooms ?? 0}
                                  bathrooms={message.properties?.bathrooms ?? 0}
                                  plotArea={message.properties?.plotArea} // plotArea is already optional
                                  // --- Pass condition and epcValue from sellingState (still needed for loader text) ---
                                  condition={sellingState.propertyInfo?.condition || ''}
                                  epcValue={sellingState.propertyInfo?.epcValue || ''}
                                  // --- Pass additional details DIRECTLY from the message properties ---
                                  additionalDetails={message.properties?.additionalDetails}
                                  // --- Pass the overall current workflow step ---
                                  currentAvmWorkflowStep={currentWorkflowStep} // Use the variable calculated for the progress bar
                                />
                              ) : (
                              // --- Otherwise, render standard text message --- 
                                <div dangerouslySetInnerHTML={{ 
                                  __html: formatMessage(message.content)
                                }} />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="fixed z-40 pointer-events-auto mx-auto max-w-[350px] bottom-4 w-full md:left-auto md:translate-x-0 md:bottom-6 md:right-6 md:w-auto md:max-w-none transition-transform duration-200 ease-in-out hover:scale-[1.025]"
            >
              {/* Top Component - Positioned directly above the widget */}
              {urlConfig?.top && (
                <div className="absolute bottom-full left-0 right-0 mb-2">
                  {React.createElement(urlConfig.top, {
                    onOpenChat: () => setIsChatOpen(true)
                  })}
                </div>
              )}

              {/* Left Component - Positioned directly to the left of the widget */}
              {urlConfig?.left && (
                <div className="absolute right-full top-0 bottom-0 mr-2 flex items-center">
                  {React.createElement(urlConfig.left, {
                    onOpenChat: () => setIsChatOpen(true)
                  })}
                </div>
              )}

              <div 
                className="relative w-full rounded-xl bg-white shadow-xl px-4 py-4 flex flex-col gap-2 cursor-pointer border border-zinc-200 md:p-4 md:w-96"
                onClick={() => setIsChatOpen(true)}
                style={urlConfig?.styling}
              >
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); // Prevent the container's onClick from firing
                    setIsReady(false); // Hide the widget when close is clicked
                    sendResizeMessage('0px', '0px');
                  }}
                  className={`absolute -top-2 -right-2 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center text-black hover:text-gray-700 transition-colors shadow-sm border border-gray-300 z-10 ${
                    urlConfig?.closedWidgetCloseButtonClassName || 'w-5 h-5'
                  }`}
                  aria-label="Close widget" // Add accessibility label
                >
                  <XMarkIcon className={`${urlConfig?.closedWidgetCloseIconClassName || 'h-3 w-3'}`} />
                </button>
                  
                <div className="flex items-start justify-between gap-2">
                  <p className='text-base font-semibold text-gray-900 text-left'>
                    {urlConfig?.widgetTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.widgetTitleOverrideKey]
                      ? dictionary.sellingForm[urlConfig.widgetTitleOverrideKey]
                      : dictionary?.sellingForm?.widgetTitle || 'Determine the value of your home...'}
                  </p>
                  <span className="bg-gray-200 px-3 py-1 rounded-full ml-1">
                    <span className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-zinc-700 via-white to-zinc-700 animate-[shimmer_3s_linear_infinite] bg-[length:200%_auto]">
                        AI
                    </span>
                  </span>
                </div>

                <div className="flex items-center justify-between mt-1 md:mt-2">
                  {chatFaviconUrl && (
                    <Image 
                      src={chatFaviconUrl} // Use state directly
                      alt="Agent Avatar" 
                      width={40} 
                      height={40} 
                      className="rounded-full" 
                      unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                    />
                  )}

                  <button
                    className="flex items-center justify-center gap-1 md:gap-2 bg-zinc-800 text-white px-3 py-2 rounded-full shadow-md hover:bg-zinc-700 transition-all duration-200 text-sm pointer-events-none"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                      <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
                    </svg>
                    <span className="font-medium">{dictionary?.sellingForm?.continueButton || 'Start'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default AvmAgent;